<template>
  <label class="toggle">
    <input
      type="checkbox"
      class="toggle-input"
      :checked="modelValue"
      :disabled="isDisabled"
      :aria-label="ariaLabel || undefined"
      @change="
        $emit('update:modelValue', ($event.target as HTMLInputElement).checked)
      " />
    <span class="toggle-track" aria-hidden="true"></span>
    <span v-if="label">{{ label }}</span>
  </label>
</template>

<script lang="ts">
export default {
  name: 'Toggle',

  emits: ['update:modelValue'],

  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    label: {
      type: String,
      default: '',
    },
    /**
     * Names the control for screen readers where the visible label is not this component's own —
     * a settings row that puts the label on the left and the toggle on the right. Without it
     * such a toggle has no accessible name at all.
     */
    ariaLabel: {
      type: String,
      default: '',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style scoped>
/* Sliding switch track + thumb — needs a pseudo-element thumb and a checked-state
   position transition, which can't be expressed with Tailwind utilities alone.
   Matches the canonical .form-toggle spec in
   lab-design/surfaces/lab-management/components/input.md */
.toggle {
  display: flex;
  align-items: center;
  gap: var(--space-base);
  font-size: var(--type-body-sm-size);
  color: var(--color-text-primary);
  cursor: pointer;
  user-select: none;
}
.toggle-input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.toggle-track {
  width: 36px;
  height: 20px;
  background: var(--color-border-subtle);
  border-radius: var(--radius-pill);
  position: relative;
  flex-shrink: 0;
  transition: background var(--motion-duration-fast) var(--motion-easing);
}
.toggle-track::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: var(--color-surface);
  border-radius: var(--radius-pill);
  transition: left var(--motion-duration-fast) var(--motion-easing);
}
.toggle-input:checked + .toggle-track {
  background: var(--color-action);
}
.toggle-input:checked + .toggle-track::after {
  left: 18px;
}
.toggle:has(.toggle-input:disabled) {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
